import React from 'react';

import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { IOffer } from '@rbi-ctg/menu';
import { Tile } from 'components/features/components/marketing-tile-group/marketing-tile-group';
import LoadingAnimation from 'components/loading-animation';
import { HorizontalPosition, TileSize } from 'components/marketing-tile';
import { OfferCell } from 'components/offer/ui-refresh/cell';
import Picture from 'components/picture';
import { useEnablePullToReloadOnScroll } from 'components/pull-to-reload';
import { useOffersPageMarketingTiles } from 'hooks/use-combine-offers-and-marketing-tiles';
import useMediaQuery, { MediaQuery } from 'hooks/use-media-query';
import { LoyaltyOffersCooldown } from 'pages/loyalty/loyalty-offers/loyalty-offers-cooldown';
import { LoyaltyPullToReload } from 'pages/loyalty/loyalty-pull-to-reload';
import { selectors, useAppSelector } from 'state/global-state';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useOffersContext } from 'state/offers';
import { useOrderContext } from 'state/order';
import { useStoreContext } from 'state/store';
import { getUniqIdForOffer, sortOffersForSelectedServiceMode } from 'utils/offers';

import { LOADING_ANIMATION_AS_REFRESH_CONTENT } from './constants';
import { EndOfOffers } from './end-of-offers';
import { OfferSelected } from './offer-selected';
import { useOffersUIContext } from './offers-ui-context';
import { SelectRestaurantCta } from './select-restaurant-cta';
import theme from './theme';

const LoadingAnimationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 3rem;
`;

const ContentContainer = styled.div`
  height: 100%;
  overflow-y: scroll;
  ${Styles.desktop`
    position: relative;
    width: 100%;
  `}
`;

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  margin-top: 120px;

  ${Styles.desktop`
    margin-top: 0;
  `}
`;

const EmptyStateImage = styled(Picture)`
  height: 107px;
  width: 95px;
`;

const EmptyStateTitle = styled.h3`
  margin: 16px 0 4px;

  text-transform: uppercase;
  font-family: ${Styles.fontFamily.base};
  color: ${Styles.color.black};
  font-size: 1.5rem;
  text-align: center;
`;

const EmptyStateBody = styled.p`
  max-width: 215px;
  margin: 0;
  font-family: ${Styles.fontFamily.body};
  color: ${theme.emptyStateBodyColor};
  text-align: center;
  font-size: 0.9rem;
  line-height: 1.3rem;
`;

const List = styled.ul`
  list-style-type: none;
  margin: 0;
  padding: 0;
`;

export const BrowsingPanel: React.FC = () => {
  const { formatMessage } = useIntl();
  const enableSortOffersForServiceMode = useFlag(
    LaunchDarklyFlag.ENABLE_SORT_OFFERS_FOR_SERVICE_MODE
  );
  const hideOffersForServiceMode = useFlag(LaunchDarklyFlag.ENABLE_HIDE_OFFERS_FOR_SERVICE_MODE);
  const loyaltyOffersInCooldown = useAppSelector(selectors.loyalty.selectLoyaltyOffersInCooldown);
  const { noStoreSelected } = useStoreContext();
  const { selectedOffer, clearSelectedOffer } = useOffersContext();
  const {
    featureOffers,
    loading,
    showEmptyState,
    offers,
    offersFeedback = {},
    onSelectOffer,
    previewOfferId,
    refreshOffers,
  } = useOffersUIContext();
  const { serviceMode } = useOrderContext();

  const {
    getScrollPositionForReload,
    pullToReloadEnabled,
    setScrollRef,
  } = useEnablePullToReloadOnScroll();

  const isMobile = useMediaQuery(MediaQuery.Mobile);
  const enableSelectRestaurantCta = useFlag(LaunchDarklyFlag.ENABLE_OFFERS_SELECT_RESTAURANT_CTA);
  const { offersBrowsingPanel = null, offersPageMarketingTiles = [] } = featureOffers || {};

  const filterSelectedOffer = (offer: IOffer) =>
    getUniqIdForOffer({ id: offer._id, token: offer.tokenId }) !== selectedOfferUniqueId;

  const minOffers = offersBrowsingPanel?.offersSelectRestaurantCta?.minimumNumberOfOffers ?? null;
  const endOfOffersImage = offersBrowsingPanel?.endOfOffersImage ?? null;
  const endOfOffersMessage = offersBrowsingPanel?.endOfOffersMessage ?? null;
  const emptyStateTitle = offersBrowsingPanel?.emptyStateTitle ?? null;
  const emptyStateBody = offersBrowsingPanel?.emptyStateBody ?? null;
  const emptyStateImage = offersBrowsingPanel?.emptyStateImage ?? null;

  const selectedOfferUniqueId =
    selectedOffer &&
    getUniqIdForOffer({
      id: selectedOffer._id,
      token: selectedOffer.tokenId,
    });

  // sort offers by SM based on LD flag & if user has selected a SM
  const shouldSortOffersForServiceMode =
    (enableSortOffersForServiceMode || hideOffersForServiceMode) && !!serviceMode;
  const offersToDisplay = shouldSortOffersForServiceMode
    ? sortOffersForSelectedServiceMode(offers, serviceMode, hideOffersForServiceMode)
    : offers;

  // True when offers with no restaurant selection is less than min offers specified in sanity.
  // will hide offers and show CTA card prompting restaurant selection to unlock restaurant restricted offers.
  const availableOffersLessThanMinimum =
    enableSelectRestaurantCta && noStoreSelected && minOffers && offers.length < minOffers;

  const {
    sortedTilesAndOffers,
    marketingTileTop,
    marketingTileBottom,
  } = useOffersPageMarketingTiles({
    offers: offersToDisplay,
    offersPageMarketingTiles,
    selectedOffer,
  });

  const offersAndTiles = sortedTilesAndOffers
    .filter(filterSelectedOffer)
    .map((offerOrTile: any, index: number) => {
      const isOffer = offerOrTile?._type === 'offer';
      if (isOffer) {
        return (
          <OfferCell
            key={`${offerOrTile._id}-${index}`}
            offer={offerOrTile}
            offerFeedback={offersFeedback[offerOrTile._id]}
            onClick={onSelectOffer}
            isSelected={!isMobile && previewOfferId === offerOrTile._id}
          />
        );
      }
      return (
        <Tile
          tile={offerOrTile}
          key={offerOrTile?._id}
          size={TileSize.HALF}
          buttonPosition={HorizontalPosition.RIGHT}
          hasBorder={false}
          additionalPadding={`0.5rem`}
        />
      );
    });

  if (loading && !offers.length) {
    return (
      <LoadingAnimationContainer data-testid="browsing-panel-loading-animation">
        <LoadingAnimation fillColor={Styles.color.primary} />
      </LoadingAnimationContainer>
    );
  }

  if (showEmptyState) {
    return (
      <ContentContainer data-testid="browsing-panel-empty-state">
        <EmptyStateContainer>
          {emptyStateImage && (
            <EmptyStateImage
              image={emptyStateImage}
              alt={formatMessage({ id: 'noNewOffers' })}
              objectFitContain
            />
          )}
          {emptyStateTitle && (
            <EmptyStateTitle data-testid="browsing-panel-empty-state-title">
              {emptyStateTitle}
            </EmptyStateTitle>
          )}
          {emptyStateBody && (
            <EmptyStateBody data-testid="browsing-panel-empty-state-body">
              {emptyStateBody}
            </EmptyStateBody>
          )}
        </EmptyStateContainer>
      </ContentContainer>
    );
  }

  return (
    <ContentContainer
      data-testid="browsing-panel"
      onScroll={getScrollPositionForReload}
      ref={setScrollRef}
    >
      <LoyaltyPullToReload
        onRefresh={refreshOffers}
        enabled={pullToReloadEnabled}
        refreshContent={
          LOADING_ANIMATION_AS_REFRESH_CONTENT ? (
            <LoadingAnimationContainer>
              <LoadingAnimation fillColor={Styles.color.primary} />
            </LoadingAnimationContainer>
          ) : undefined
        }
      >
        {loyaltyOffersInCooldown && <LoyaltyOffersCooldown />}
        <List>
          {marketingTileTop && (
            <Tile
              data-testid="top-marketing-tile"
              tile={marketingTileTop}
              size={TileSize.HALF}
              buttonPosition={HorizontalPosition.RIGHT}
              hasBorder={false}
              additionalPadding={`0.5rem`}
            />
          )}
          {selectedOffer && (
            <OfferSelected
              data-testid="selected-offer"
              offer={selectedOffer}
              onRemoveSelectedOffer={() => {
                clearSelectedOffer({ doLog: true, popToast: true });
              }}
              onClick={onSelectOffer}
              isSelected={!isMobile && previewOfferId === selectedOffer._id}
            />
          )}
          {!availableOffersLessThanMinimum && !!offersAndTiles.length && offersAndTiles}
          {marketingTileBottom && (
            <Tile
              data-testid="bottom-marketing-tile"
              tile={marketingTileBottom}
              size={TileSize.HALF}
              buttonPosition={HorizontalPosition.RIGHT}
              hasBorder={false}
              additionalPadding={`0.5rem`}
            />
          )}
          {enableSelectRestaurantCta && <SelectRestaurantCta />}
          {endOfOffersMessage && (
            <EndOfOffers message={endOfOffersMessage} image={endOfOffersImage} />
          )}
        </List>
      </LoyaltyPullToReload>
    </ContentContainer>
  );
};
